/**
 * @file AssetPortalsPanel.js
 * @description This panel populates the portals associated with an asset.
 */

import { useState, useEffect } from 'react';
import styles from '../AssetDrawer.module.scss';
import { getBadgeStatus } from '../../helpers/getBadgeStatus';
import { fetchPopulationGroups } from '../../../services/populationGroupService';
import { getClient } from '../../../services/clientService';
import { getAgencies } from '../../../services/systemService';
import { useNavigate } from 'react-router';
import { SecurityAttributes } from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/AuthContext';
import { ROUTES } from '../../../constants/routes';
import { handleNavigateToClient } from '../../../utils/utils';
import { getText } from '../../../i18n';

const AssetPortalsPanel = ({ asset }) => {
    const navigate = useNavigate();
    const { hasAttribute } = useAuthContext();
    const [sortedPortals, setSortedPortals] = useState([]);
    const [sideloadedGroupsByID, setSideloadedGroupsByID] = useState(null);
    const [clientsByTenantId, setClientsByTenantId] = useState({});
    const [agenciesById, setAgenciesById] = useState({});

    useEffect(() => {
        const loadPopulationGroups = async () => {
            try {
                const fetchedPopGroups = await fetchPopulationGroups();
                const groupsByID = fetchedPopGroups.reduce((acc, group) => {
                    acc[group.id] = group;
                    return acc;
                }, {});

                setSideloadedGroupsByID(groupsByID);
            } catch (error) {
                console.error('Could not fetch population groups', error);
            }
        };

        const loadClients = async portals => {
            try {
                const uniqueTenantIds = [
                    ...new Set(
                        portals
                            .map(portal => portal.tenantId)
                            .filter(tenantId => Boolean(tenantId)),
                    ),
                ];

                if (uniqueTenantIds.length === 0) {
                    setClientsByTenantId({});
                    return;
                }

                const clientResults = await Promise.allSettled(
                    uniqueTenantIds.map(async tenantId => {
                        const client = await getClient(tenantId);
                        return {
                            tenantId,
                            name: client?.name || '',
                            companyCode: client?.clientCode,
                        };
                    }),
                );

                const nextClientsByTenantId = clientResults.reduce(
                    (acc, result) => {
                        if (result.status === 'fulfilled') {
                            acc[result.value.tenantId] = {
                                name: result.value.name,
                                companyCode: result.value.companyCode,
                            };
                        }
                        return acc;
                    },
                    {},
                );

                setClientsByTenantId(nextClientsByTenantId);
            } catch (error) {
                console.error('Could not fetch portal clients', error);
            }
        };

        const loadAgencies = async portals => {
            try {
                const getPortalAgencyId = portal => {
                    if (
                        portal.agencyId !== undefined &&
                        portal.agencyId !== null
                    ) {
                        return portal.agencyId;
                    }

                    return null;
                };

                const uniqueAgencyIds = [
                    ...new Set(
                        portals
                            .map(portal => getPortalAgencyId(portal))
                            .filter(
                                agencyId =>
                                    agencyId !== undefined && agencyId !== null,
                            ),
                    ),
                ];

                if (uniqueAgencyIds.length === 0) {
                    setAgenciesById({});
                    return;
                }

                const fetchedAgencies = await getAgencies();
                const agencyViewModels = fetchedAgencies?.viewModels || [];
                const requiredAgencyIdSet = new Set(
                    uniqueAgencyIds.map(agencyId => String(agencyId)),
                );

                const nextAgenciesById = agencyViewModels.reduce(
                    (acc, agency) => {
                        const agencyId = String(agency.id);

                        if (!requiredAgencyIdSet.has(agencyId)) return acc;

                        acc[agencyId] = {
                            name: agency?.name || '',
                        };
                        return acc;
                    },
                    {},
                );

                setAgenciesById(nextAgenciesById);
            } catch (error) {
                console.error('Could not fetch portal agencies', error);
            }
        };

        if (asset) {
            let sortedPortalsArray = [...asset.portals];
            sortedPortalsArray.sort((a, b) => b.order - a.order);
            setSortedPortals(sortedPortalsArray);
            loadPopulationGroups();
            loadClients(sortedPortalsArray);
            loadAgencies(sortedPortalsArray);
        }
    }, [asset]);

    const getName = index => {
        let name = '';
        name = sortedPortals[index].name;
        return name;
    };

    const getId = index => {
        let id = 1;
        id = sortedPortals[index].id;
        return id;
    };

    const getStatus = index => {
        let status = 1;
        status = sortedPortals[index].status;
        return status;
    };

    const getPopulationGroups = index => {
        let populationGroups = [];
        populationGroups = sortedPortals[index].populationGroups;
        return populationGroups;
    };

    const getTenantId = index => {
        let tenantId = null;
        tenantId = sortedPortals[index].tenantId;
        return tenantId;
    };

    const getAgencyId = index => {
        const portal = sortedPortals[index];

        if (portal.agencyId !== undefined && portal.agencyId !== null) {
            return portal.agencyId;
        }

        return null;
    };

    const getClientName = index => {
        const tenantId = getTenantId(index);
        const client = clientsByTenantId[tenantId];

        if (!tenantId) return '';
        if (client?.name) return client.name;
        return 'Unknown';
    };

    const getAgencyName = index => {
        const portal = sortedPortals[index];
        const agencyNameFromPortal =
            portal.agencyName ||
            portal?.agency?.name ||
            portal.agencyTemplateName ||
            portal?.agencyTemplate?.name;

        if (agencyNameFromPortal) return agencyNameFromPortal;

        const agencyId = getAgencyId(index);
        if (agencyId === undefined || agencyId === null) return 'Unknown';

        const agency = agenciesById[String(agencyId)];
        if (agency?.name) return agency.name;

        return 'Unknown';
    };

    const getPortalOwnerName = index => {
        const tenantId = getTenantId(index);

        if (tenantId) return getClientName(index);

        return getAgencyName(index);
    };

    const handleViewPortal = index => {
        const portalId = getId(index);
        const tenantId = getTenantId(index);
        const client = clientsByTenantId[tenantId];

        if (tenantId && asset.isGlobalAsset) {
            handleNavigateToClient(
                tenantId,
                client?.companyCode,
                client?.name,
                false,
                false,
            );
        }

        navigate(ROUTES.PORTAL_PAGES_NEW, {
            state: {
                id: portalId,
            },
        });

        window.location.reload();
    };

    return (
        <>
            <p className={styles.portalsPanelTitle}>{getText('msg_a0c88242e813')}</p>
            <div className={styles.assetPortalsPanel}>
                <div className={styles.portalsContent}>
                    {sortedPortals.map((event, index) => (
                        <div key={event.id} className={styles.portalsEntry}>
                            <div className={styles.portalsHeader}>
                                <div style={{ marginBottom: '10px' }}>
                                    <span className={styles.portalsName}>
                                        {getName(index)}
                                    </span>
                                    {hasAttribute(
                                        SecurityAttributes.PortalsFullAccess,
                                    ) && (
                                        <p
                                            className={styles.heading}
                                            onClick={() =>
                                                handleViewPortal(index)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                color: '#007BFF',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {getText('msg_69bd4ef9fbd0')}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <span className={styles.portalsStatus}>
                                        {getText('msg_bae7d5be7082')}
                                    </span>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <span
                                        className={`item-page-status badge-base ${getBadgeStatus().Class(getStatus(index))}`}
                                    >
                                        <svg
                                            className="me-1"
                                            width={8}
                                            height={8}
                                            viewBox="0 0 8 8"
                                            fill="none"
                                        >
                                            <circle
                                                cx={4}
                                                cy={4}
                                                r={3}
                                                fill={getBadgeStatus().SVGColor(
                                                    getStatus(index),
                                                )}
                                            />
                                        </svg>
                                        {getBadgeStatus().PortalStatusString(
                                            getStatus(index),
                                        )}
                                    </span>
                                </div>
                                <div>{getText('msg_e744dd6b3c48')}</div>
                                <div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            gap: '6px',
                                            maxWidth: '300px',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {getPopulationGroups(index).map(
                                            (groupID, index) => (
                                                <span
                                                    key={`${event.id}-${groupID}-${index}`}
                                                    className="badge-base badge-population-group"
                                                >
                                                    {sideloadedGroupsByID !==
                                                    null
                                                        ? sideloadedGroupsByID[
                                                              groupID
                                                          ]?.name || ''
                                                        : ''}
                                                </span>
                                            ),
                                        )}
                                    </div>
                                </div>
                                {asset.isGlobalAsset && (
                                    <div>
                                        <span
                                            className={styles.portalsStatus}
                                            style={{
                                                marginRight: '4px',
                                            }}
                                        >
                                            {getTenantId(index)
                                                ? 'Client:'
                                                : 'Agency Template:'}
                                        </span>
                                        {getPortalOwnerName(index)}
                                    </div>
                                )}
                                <hr />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default AssetPortalsPanel;
